import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Mail, Send, Loader2, CheckCircle, AlertTriangle, MessageSquare, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_TEMPLATE = {
  subject: "Votre simulation SoViewFX – {{projectName}}",
  body: "Bonjour,\n\nVeuillez trouver ci-joint votre simulation réalisée avec SoViewFX.\n\n{{message}}\n\nBien cordialement,\n\n{{signature}}",
  signature: "L'équipe SoViewFX"
};

export default function ShareModal({
  isOpen,
  onClose,
  projectId,
  simulationId,
  projectName,
  simulatedImageUrl,
  originalImageUrl,
  simulationParameters,
  createdAt
}) {
  const { token } = useAuthStore();
  const emailRef = useRef(null);

  const [page, setPage] = useState('FORM'); // FORM | SENDING | SENT | ERROR
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  // Reset form & load template when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setPage('FORM');
    setRecipientEmail('');
    setCustomMessage('');
    setInternalNotes('');
    setErrors({});
    setErrorMessage('');

    const loadTemplate = async () => {
      const apply = (data) => {
        const sig = data.signature || DEFAULT_TEMPLATE.signature;
        setEmailSubject(
          (data.subject || DEFAULT_TEMPLATE.subject)
            .replace(/\{\{projectName\}\}/g, projectName)
            .replace(/\{\{signature\}\}/g, sig)
        );
        setEmailBody(
          (data.body || DEFAULT_TEMPLATE.body)
            .replace(/\{\{projectName\}\}/g, projectName)
            .replace(/\{\{signature\}\}/g, sig)
        );
      };

      try {
        const res = await fetch(`${API_URL}/api/account/share-email-template`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          apply(data);
        } else if (res.status === 404) {
          apply(DEFAULT_TEMPLATE);
        } else {
          throw new Error('Failed to load template');
        }
      } catch {
        apply(DEFAULT_TEMPLATE);
      }
    };

    loadTemplate();
    setTimeout(() => emailRef.current?.focus(), 350);
  }, [isOpen, projectName]);

  const validate = () => {
    const errs = {};
    if (!recipientEmail.trim()) {
      errs.recipientEmail = "L'email est requis";
    } else if (!EMAIL_REGEX.test(recipientEmail.trim())) {
      errs.recipientEmail = 'Email invalide';
    }
    if (!simulatedImageUrl) errs.image = "L'image simulée est requise";
    if (!projectId) errs.projectId = 'ID de projet requis';
    if (!simulationId) errs.simulationId = 'ID de simulation requis';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) return;
    setPage('SENDING');

    try {
      const res = await fetch(`${API_URL}/api/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId,
          simulationId,
          recipientEmail: recipientEmail.trim(),
          customMessage: customMessage || undefined,
          internalNotes: internalNotes || undefined,
          simulatedImageUrl,
          originalImageUrl: originalImageUrl || undefined,
          simulationParameters
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Échec de l'envoi");
      }

      setPage('SENT');
      toast.success(`Simulation envoyée à ${recipientEmail.trim()} !`);
    } catch (err) {
      setErrorMessage(err.message || "Erreur lors de l'envoi");
      setPage('ERROR');
    }
  };

  const handleReset = () => {
    setPage('FORM');
    setRecipientEmail('');
    setCustomMessage('');
    setInternalNotes('');
    setErrors({});
    setErrorMessage('');
  };

  const handleEmailBlur = () => {
    const val = recipientEmail.trim();
    if (val && !EMAIL_REGEX.test(val)) {
      setErrors(p => ({ ...p, recipientEmail: 'Email invalide' }));
    } else {
      setErrors(p => {
        const next = { ...p };
        delete next.recipientEmail;
        return next;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={onClose}
      />

      {/* Sheet modal */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="sheet"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-lg text-white">
            Partager la simulation
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <X size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* ───────────────── SENT ───────────────── */}
        {page === 'SENT' && (
          <div className="py-8 text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-white mb-2">
              Simulation envoyée !
            </h3>
            <p className="text-muted-foreground mb-2">
              Un email a été envoyé à{' '}
              <span className="text-primary font-semibold">{recipientEmail}</span>
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              Vous recevrez également une copie et un récapitulatif interne
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium hover:bg-white/10 transition-colors"
              >
                Partager une autre
              </button>
              <button onClick={onClose} className="flex-1 btn-primary">
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* ───────────────── ERROR ───────────────── */}
        {page === 'ERROR' && (
          <div className="py-8 text-center">
            <AlertTriangle size={64} className="text-red-400 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-white mb-2">Erreur</h3>
            <p className="text-muted-foreground mb-8">{errorMessage}</p>
            <button onClick={handleSend} className="btn-primary w-full">
              Réessayer
            </button>
          </div>
        )}

        {/* ───────────────── FORM / SENDING ───────────────── */}
        {(page === 'FORM' || page === 'SENDING') && (
          <div className="space-y-4">
            {/* Preview image */}
            {simulatedImageUrl && (
              <div className="card overflow-hidden">
                <img
                  src={simulatedImageUrl}
                  alt="Simulation"
                  className="w-full h-32 object-cover"
                />
              </div>
            )}

            {/* Recipient Email (required) */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email du destinataire <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={emailRef}
                  type="email"
                  value={recipientEmail}
                  onChange={e => {
                    setRecipientEmail(e.target.value);
                    setErrors(p => ({ ...p, recipientEmail: '' }));
                  }}
                  onBlur={handleEmailBlur}
                  disabled={page === 'SENDING'}
                  className={`input pl-10 ${errors.recipientEmail ? 'border-red-400' : ''}`}
                  placeholder="client@exemple.fr"
                />
              </div>
              {errors.recipientEmail && (
                <p className="text-xs text-red-400 mt-1">{errors.recipientEmail}</p>
              )}
            </div>

            {/* Email Subject (pre-filled from template) */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Objet de l'email
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                disabled={page === 'SENDING'}
                className="input"
                placeholder="Objet de l'email"
              />
            </div>

            {/* Email Body (pre-filled from template) */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Corps de l'email
              </label>
              <textarea
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
                disabled={page === 'SENDING'}
                className="input min-h-[120px] resize-none pt-3"
                placeholder="Corps de l'email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Vous pouvez utiliser {'{{projectName}}'}, {'{{message}}'}, {'{{signature}}'}
              </p>
            </div>

            {/* Custom Message (optional) */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Message personnalisé{' '}
                <span className="text-muted-foreground text-xs">(optionnel)</span>
              </label>
              <div className="relative">
                <MessageSquare size={16} className="absolute left-3 top-3 text-muted-foreground" />
                <textarea
                  value={customMessage}
                  onChange={e =>
                    e.target.value.length <= 1000 && setCustomMessage(e.target.value)
                  }
                  disabled={page === 'SENDING'}
                  className="input pl-10 min-h-[80px] resize-none pt-3"
                  placeholder="Un message personnel pour le destinataire..."
                />
              </div>
              <p className="text-xs text-muted-foreground text-right mt-1">
                {customMessage.length}/1000
              </p>
            </div>

            {/* Internal Notes (optional) */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Notes internes{' '}
                <span className="text-muted-foreground text-xs">(optionnel)</span>
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-3 text-muted-foreground" />
                <textarea
                  value={internalNotes}
                  onChange={e =>
                    e.target.value.length <= 2000 && setInternalNotes(e.target.value)
                  }
                  disabled={page === 'SENDING'}
                  className="input pl-10 min-h-[60px] resize-none pt-3"
                  placeholder="Notes internes..."
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Ces notes sont internes et ne seront pas envoyées au destinataire
              </p>
              <p className="text-xs text-muted-foreground text-right">
                {internalNotes.length}/2000
              </p>
            </div>

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={page === 'SENDING'}
              className="btn-primary w-full flex items-center justify-center gap-2 py-4 disabled:opacity-50"
            >
              {page === 'SENDING' ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Envoyer la simulation
                </>
              )}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
